import { useQuery } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { getReadProvider } from './useProvider'
import { CHALLENGE_FACTORY_ABI } from '../abis/ChallengeFactoryABI'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'
import { CHALLENGE_STATE, getCurrentRound, formatAddress } from '../types/challenge'

const ZERO_BYTES32 = '0x' + '00'.repeat(32)

export interface MyPageParticipatingItem {
  address: string
  name: string
  state: number
  challengeType: number
  entryFee: bigint
  participantCount: number
  maxParticipants: number
  startTime: number
  endTime: number
  roundDeadlines: number[]
  currentRound: number
  todayProofDone: boolean
}

export interface MyPageCreatedItem {
  address: string
  name: string
  state: number
  participantCount: number
  maxParticipants: number
  startTime: number
  endTime: number
  pendingReportsCount: number
}

export interface MyPageCompletedItem {
  address: string
  name: string
  state: number
  challengeType: number
  entryFee: bigint
  isCompleted: boolean
  isWinner: boolean
  isClaimed: boolean
  payoutPerWinner: bigint
}

export interface MyPageData {
  participating: MyPageParticipatingItem[]
  created: MyPageCreatedItem[]
  completed: MyPageCompletedItem[]
}

interface ReportItem {
  status: string
}

async function fetchMyPageData(userAddress: string): Promise<MyPageData> {
  const provider = getReadProvider()
  const factoryAddress = (import.meta.env.VITE_FACTORY_ADDRESS as string | undefined) ?? ''
  if (!factoryAddress) return { participating: [], created: [], completed: [] }

  const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, provider)

  // One call lists every challenge — no event-log scanning.
  const addresses = (await (factory.getChallenges as () => Promise<string[]>)()).map((a) =>
    a.toLowerCase(),
  )
  if (addresses.length === 0) return { participating: [], created: [], completed: [] }

  // On-chain basics (money / rules / membership) for every challenge.
  const challengeBasics = await Promise.all(
    addresses.map(async (address) => {
      const contract = new ethers.Contract(address, CHALLENGE_ABI, provider)
      const [
        state,
        creator,
        challengeType,
        startTime,
        endTime,
        entryFee,
        participantCount,
        maxParticipants,
      ] = await Promise.all([
        contract.state() as Promise<bigint>,
        contract.creator() as Promise<string>,
        contract.challengeType() as Promise<bigint>,
        contract.startTime() as Promise<bigint>,
        contract.endTime() as Promise<bigint>,
        contract.entryFee() as Promise<bigint>,
        contract.participantCount() as Promise<bigint>,
        contract.maxParticipants() as Promise<bigint>,
      ])
      return {
        address,
        name: '',
        creator: creator.toLowerCase(),
        challengeType: Number(challengeType),
        state: Number(state),
        startTime: Number(startTime),
        endTime: Number(endTime),
        entryFee: BigInt(entryFee.toString()),
        participantCount: Number(participantCount),
        maxParticipants: Number(maxParticipants),
      }
    }),
  )

  // Batch-check isParticipant for each challenge
  const participationFlags = await Promise.all(
    challengeBasics.map((ch) => {
      const contract = new ethers.Contract(ch.address, CHALLENGE_ABI, provider)
      return contract.isParticipant(userAddress) as Promise<boolean>
    }),
  )

  // Display names live off-chain (KV) — fetch only for challenges relevant to this user.
  const userLc = userAddress.toLowerCase()
  const namesApiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
  await Promise.all(
    challengeBasics.map(async (ch, i) => {
      if (ch.creator !== userLc && !participationFlags[i]) return
      try {
        const r = await fetch(`${namesApiBase}/api/challenges/${ch.address}`)
        if (r.ok) {
          const d = (await r.json()) as { title?: string }
          if (d.title) ch.name = d.title
        }
      } catch {
        // non-fatal
      }
      if (!ch.name) ch.name = formatAddress(ch.address)
    }),
  )

  const participating: MyPageParticipatingItem[] = []
  const created: MyPageCreatedItem[] = []
  const completed: MyPageCompletedItem[] = []
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

  await Promise.all(
    challengeBasics.map(async (ch, i) => {
      const isParticipant = participationFlags[i]
      const isCreator = ch.creator === userAddress.toLowerCase()

      if (isCreator) {
        let pendingReportsCount = 0
        try {
          const resp = await fetch(`${apiBase}/api/reports?challenge=${ch.address}`)
          if (resp.ok) {
            const data = (await resp.json()) as { reports?: ReportItem[] }
            pendingReportsCount = (data.reports ?? []).filter((r) => r.status === 'pending').length
          }
        } catch {
          // non-fatal
        }
        created.push({
          address: ch.address,
          name: ch.name,
          state: ch.state,
          participantCount: ch.participantCount,
          maxParticipants: ch.maxParticipants,
          startTime: ch.startTime,
          endTime: ch.endTime,
          pendingReportsCount,
        })
      }

      if (isParticipant) {
        const contract = new ethers.Contract(ch.address, CHALLENGE_ABI, provider)

        // A participant in a not-yet-ended challenge (PENDING/ACTIVE) — or a failed/refundable
        // one (REFUNDED) so they can find it to reclaim their entry fee — is "참가 중".
        if (
          ch.state === CHALLENGE_STATE.ACTIVE ||
          ch.state === CHALLENGE_STATE.PENDING ||
          ch.state === CHALLENGE_STATE.REFUNDED
        ) {
          const roundDeadlinesBig = await (contract.getRoundDeadlines() as Promise<bigint[]>)
          const roundDeadlines = roundDeadlinesBig.map(Number)
          const currentRound = getCurrentRound(roundDeadlines)
          const effectiveRound = Math.min(currentRound, Math.max(0, roundDeadlines.length - 1))

          let todayProofDone = false
          if (roundDeadlines.length > 0) {
            const hash = await (contract.proofHash(userAddress, effectiveRound) as Promise<string>)
            todayProofDone = hash !== ZERO_BYTES32
          }

          participating.push({
            address: ch.address,
            name: ch.name,
            state: ch.state,
            challengeType: ch.challengeType,
            entryFee: ch.entryFee,
            participantCount: ch.participantCount,
            maxParticipants: ch.maxParticipants,
            startTime: ch.startTime,
            endTime: ch.endTime,
            roundDeadlines,
            currentRound: effectiveRound,
            todayProofDone,
          })
        } else if (ch.state === CHALLENGE_STATE.ENDED) {
          const [isEliminated, isWinnerBool, isClaimed, payoutPerWinner] = await Promise.all([
            contract.eliminated(userAddress) as Promise<boolean>,
            contract.isWinner(userAddress) as Promise<boolean>,
            contract.claimed(userAddress) as Promise<boolean>,
            contract.payoutPerWinner() as Promise<bigint>,
          ])

          completed.push({
            address: ch.address,
            name: ch.name,
            state: ch.state,
            challengeType: ch.challengeType,
            entryFee: ch.entryFee,
            isCompleted: !isEliminated,
            isWinner: isWinnerBool,
            isClaimed,
            payoutPerWinner: BigInt(payoutPerWinner.toString()),
          })
        }
      }
    }),
  )

  return { participating, created, completed }
}

export function useMyPageData(userAddress: string | null) {
  return useQuery<MyPageData>({
    queryKey: ['my-page', userAddress],
    queryFn: () => fetchMyPageData(userAddress!),
    enabled: Boolean(userAddress),
    staleTime: 30_000,
  })
}
